import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empQualificationService } from '@/services/empQualificationService'
import { employeesService } from '@/services/employeesService'
import { customersService } from '@/services/customersService'
import type { EmpQualificationVm } from '@/api/types/empQualification.types'
import type { CountryLookup, CityLookup } from '@/api/types'
import { showError, showSuccess } from '@/lib/swal'

export const EmpQualificationEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])
  const [qualificationTypes, setQualificationTypes] = useState<{ id?: number; name?: string; lookupNameEn?: string; lookupNameAr?: string }[]>([])

  const [form, setForm] = useState({
    empId: 0,
    empDegreeName: '',
    empInstituteName: '',
    empStartYear: '' as string | number,
    empEndYear: '' as string | number,
    empInstituteCountryId: 0,
    empInstituteCityId: 0,
    empQuilficationTypeId: 0,
    empObtaineGpapercentage: '',
    empDescription: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryRes = await customersService.getCountries()
        setCountries(countryRes ?? [])
      } catch {
        setCountries([])
      }
    }
    loadCountries()
  }, [])

  useEffect(() => {
    empQualificationService.getQualificationTypes().then((data) => setQualificationTypes(data ?? [])).catch(() => setQualificationTypes([]))
  }, [])

  useEffect(() => {
    if (form.empInstituteCountryId && form.empInstituteCountryId > 0) {
      const loadCities = async () => {
        try {
          const citiesData = await customersService.getCities(form.empInstituteCountryId)
          setCities(citiesData ?? [])
        } catch {
          setCities([])
        }
      }
      loadCities()
    } else {
      setCities([])
    }
  }, [form.empInstituteCountryId])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empQualificationService.getById(Number(id))
        setForm({
          empId: data.empId ?? 0,
          empDegreeName: data.empDegreeName ?? '',
          empInstituteName: data.empInstituteName ?? '',
          empStartYear: data.empStartYear ?? '',
          empEndYear: data.empEndYear ?? '',
          empInstituteCountryId: data.empInstituteCountryId ?? 0,
          empInstituteCityId: data.empInstituteCityId ?? 0,
          empQuilficationTypeId: data.empQuilficationTypeId ?? 0,
          empObtaineGpapercentage: data.empObtaineGpapercentage ?? '',
          empDescription: data.empDescription ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load qualification.')
        navigate('/qualifications')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? `Emp ${e.id}`
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees]
  )

  const countryOptions = useMemo(
    () =>
      countries.map((c) => ({
        value: String(c.id ?? 0),
        label: (c as any).lookupNameEn ?? (c as any).lookupNameAr ?? `Country ${c.id}`,
      })),
    [countries]
  )

  const cityOptions = useMemo(
    () =>
      cities.map((c) => ({
        value: String(c.id ?? 0),
        label: (c as any).lookupNameEn ?? (c as any).lookupNameAr ?? `City ${c.id}`,
      })),
    [cities]
  )

  const qualificationTypeOptions = useMemo(
    () =>
      qualificationTypes.map((t) => {
        const name = (t as any).lookupNameEn ?? (t as any).lookupNameAr ?? (t as any).name ?? `Type ${t.id}`
        return { value: String(t.id ?? 0), label: name }
      }),
    [qualificationTypes]
  )

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
    if ('empInstituteCountryId' in patch) {
      setForm((prev) => ({ ...prev, empInstituteCityId: 0 }))
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error('Please select an employee.')
      return
    }
    if (!form.empDegreeName.trim()) {
      toast.error('Degree Name is required.')
      return
    }
    if (!form.empInstituteName.trim()) {
      toast.error('Institute Name is required.')
      return
    }
    const startYear = typeof form.empStartYear === 'number' ? form.empStartYear : parseInt(String(form.empStartYear), 10)
    const endYear = typeof form.empEndYear === 'number' ? form.empEndYear : parseInt(String(form.empEndYear), 10)
    if (!Number.isFinite(startYear) || startYear < 1900) {
      toast.error('Degree Start Year is required and must be valid.')
      return
    }
    if (!Number.isFinite(endYear) || endYear < 1900) {
      toast.error('Degree End Year is required and must be valid.')
      return
    }
    if (!form.empInstituteCountryId || form.empInstituteCountryId === 0) {
      toast.error('Institute Country is required.')
      return
    }
    if (!form.empInstituteCityId || form.empInstituteCityId === 0) {
      toast.error('Institute City is required.')
      return
    }
    if (!form.empDescription.trim()) {
      toast.error('Description is required.')
      return
    }

    const payload: EmpQualificationVm = {
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      empDegreeName: form.empDegreeName.trim(),
      empInstituteName: form.empInstituteName.trim(),
      empStartYear: startYear,
      empEndYear: endYear,
      empInstituteCountryId: form.empInstituteCountryId,
      empInstituteCityId: form.empInstituteCityId,
      empQuilficationTypeId: form.empQuilficationTypeId || 0,
      empObtaineGpapercentage: form.empObtaineGpapercentage.trim() || undefined,
      empDescription: form.empDescription.trim(),
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await empQualificationService.update(payload)
        await showSuccess('Updated', 'Qualification updated successfully.')
      } else {
        await empQualificationService.create(payload)
        await showSuccess('Saved', 'Qualification saved successfully.')
      }
      navigate('/qualifications')
    } catch (e: any) {
      await showError('Save failed', e?.response?.data?.message ?? e?.message ?? 'Failed to save qualification.')
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Qualifications" description="Add or edit qualification." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Qualifications"
        description={isEditing ? 'Edit qualification' : 'Add new qualification'}
      />

      <Card>
        <CardHeader>
          <CardTitle>Qualifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select employee' }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder="Select employee"
                searchPlaceholder="Search employee..."
                emptyMessage="No employee found"
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>Degree Name *</Label>
              <Input
                value={form.empDegreeName}
                onChange={(e) => handleChange({ empDegreeName: e.target.value })}
                placeholder="Degree Name"
              />
            </div>
            <div className="space-y-2">
              <Label>Institute Name *</Label>
              <Input
                value={form.empInstituteName}
                onChange={(e) => handleChange({ empInstituteName: e.target.value })}
                placeholder="Institute Name"
              />
            </div>
            <div className="space-y-2">
              <Label>Degree Start Year *</Label>
              <Input
                type="number"
                min={1900}
                max={2100}
                value={form.empStartYear === '' ? '' : form.empStartYear}
                onChange={(e) => handleChange({ empStartYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder="e.g. 2020"
              />
            </div>
            <div className="space-y-2">
              <Label>Degree End Year *</Label>
              <Input
                type="number"
                min={1900}
                max={2100}
                value={form.empEndYear === '' ? '' : form.empEndYear}
                onChange={(e) => handleChange({ empEndYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder="e.g. 2024"
              />
            </div>
            <div className="space-y-2">
              <Label>Institute Country *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select country' }, ...countryOptions]}
                value={form.empInstituteCountryId ? String(form.empInstituteCountryId) : '0'}
                onValueChange={(v) => handleChange({ empInstituteCountryId: Number(v) || 0 })}
                placeholder="Select country"
                searchPlaceholder="Search country..."
                emptyMessage="No country found"
              />
            </div>
            <div className="space-y-2">
              <Label>Institute City *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select city' }, ...cityOptions]}
                value={form.empInstituteCityId ? String(form.empInstituteCityId) : '0'}
                onValueChange={(v) => handleChange({ empInstituteCityId: Number(v) || 0 })}
                placeholder="Select city"
                searchPlaceholder="Search city..."
                emptyMessage="No city found"
                disabled={!form.empInstituteCountryId || form.empInstituteCountryId === 0}
              />
            </div>
            <div className="space-y-2">
              <Label>Qualification Type</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select type' }, ...qualificationTypeOptions]}
                value={form.empQuilficationTypeId ? String(form.empQuilficationTypeId) : '0'}
                onValueChange={(v) => handleChange({ empQuilficationTypeId: Number(v) || 0 })}
                placeholder="Select qualification type"
                searchPlaceholder="Search..."
                emptyMessage="No type found"
              />
            </div>
            <div className="space-y-2">
              <Label>GPA / Percentage</Label>
              <Input
                value={form.empObtaineGpapercentage}
                onChange={(e) => handleChange({ empObtaineGpapercentage: e.target.value })}
                placeholder="e.g. 3.5 or 85%"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              value={form.empDescription}
              onChange={(e) => handleChange({ empDescription: e.target.value })}
              placeholder="Required example textarea"
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/qualifications')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
